import { useGenericFormStore } from '@neo/blocks/forms/persistance-store'
import { clear } from 'idb-keyval'
import type React from 'react'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import { forgetBuddyFlowConversations } from 'v2/pages/assistant/buddy-flow/flow-contact'
import { clearLocalStorageKeepingWorkspaceSidebar } from 'v2/shared/utils/workspace-sidebar-storage'
import { useAppStore } from 'v2/state/app'
import { usePhoneStore } from 'v2/state/phone'
import { BlockingModalType } from 'v2/types'
import type { RUserSession } from 'v2/types/api/auth'
import { getLocalAuth } from 'v2/utils'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { unloadGtm } from 'v2/utils/gtm'
import { logger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { authService } from '../services/auth-service'
import { primaryTabManager } from '../utils/primary-tab-manager'

export interface AuthContextType {
  userAuthenticated: boolean
  details?: RUserSession
  loginSuccess: (auth: RUserSession) => void
  logoutSuccess: () => Promise<void>
  logout: () => void
}

const initialAuth: RUserSession = JSON.parse(localStorage.getItem('auth') || '{}')
const initialValues = {
  userAuthenticated: !!initialAuth.auth_token,
}

const AuthContext = createContext<AuthContextType>(initialValues as AuthContextType)
export const useAuth = () => useContext(AuthContext)

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // https://linear.app/plivo-cx/issue/SER-1547/agent-or-login-with-multiple-tabs-open-the-app-isnot-getting-loaded
  // Temp fix: replace with useAppStore once login page is implemented
  const { setUserAuthenticated, userAuthenticated } = useAppStore()
  // const [userAuthenticated, setUserAuthenticated] = useState(initialValues.userAuthenticated)
  // Replace this with your actual login logic.
  const loginSuccess = (auth: RUserSession) => {
    try {
      // Identity only, never the session: `auth` carries auth_token and logger
      // forwards args to LogRocket and Sentry in prod.
      logger.log('Login success - setting auth object to local storage', {
        email: auth.email,
        organization_name: auth.organization_name,
        region: auth.region,
      })
      localStorage.setItem('auth', JSON.stringify(auth))
      setUserAuthenticated(true)
      // Best-effort only: this cannot revoke code GTM already ran. See gtm.ts.
      // Cannot reload here either — setAuthCookie below has not run yet.
      unloadGtm()
      primaryTabManager.init()
    } catch (error) {
      logger.error('Error setting auth', error)
    }
  }

  // Replace this with your actual logout logic.
  const logoutSuccess = async () => {
    primaryEventBus.emit(PrimaryEvents.LOGOUT)
    phoneSDK.destroy()
    window.posthog?.reset()
    logger.log('Logout success, clearing the store')
    // Buddy's chat conversation lives in sessionStorage, which logout would otherwise keep.
    // Started first: it finds the Buddy chat app from the auth that clearing storage drops.
    const buddyFlowForgotten = forgetBuddyFlowConversations()
    clearLocalStorageKeepingWorkspaceSidebar()
    queryClient?.removeQueries?.()
    queryClient?.clear?.()
    useGenericFormStore.getState().reset()
    useAppStore.getState().reset()
    window.cx.pubnubInstance?.unsubscribeAll?.()
    await Promise.all([clear(), buddyFlowForgotten])
  }

  const logout = useCallback(
    async (noWarning?: boolean) => {
      // If there is an active call, show the blocking modal
      if (usePhoneStore.getState().isCallActive && !noWarning) {
        primaryEventBus.emit(
          PrimaryEvents.TRIGGER_BLOCKING_MODAL,
          BlockingModalType.ACTIVE_CALL_LOGOUT,
        )
        return
      }
      // TODO: Add await here once multi-tab logout is implemented
      await authService.logout()
      await logoutSuccess()
      primaryEventBus.broadcast(PrimaryEvents.LOGOUT_SUCCESS)
      // Hard redirect to /login without next_url to prevent stale URL redirect
      // after logging into a different org
      window.location.href = '/login'
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    // Exposing this method to the window object so that it can be called from any part of the app.
    // This is to keep the codebase simple and to avoid prop drilling.
    window.cx.logout = logout
  }, [logout])

  const value = useMemo(
    () => ({ userAuthenticated, loginSuccess, logoutSuccess, details: getLocalAuth(), logout }),

    [userAuthenticated],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
